import * as angular from "angular";
import {firstValueFrom} from "rxjs";
import * as _ from "lodash";
import {GroupsHttpService} from "../../groups/angular/services/groups-http.service";

export interface SchedulingProjectTagsFilterCascadeResult {
  filteredGroupIds?: string[];
  groupIds: string[];
  filteredBranchIds?: string[];
  branchIds: string[];
}

export interface SchedulingProjectGroupsFilterCascadeResult {
  filteredBranchIds?: string[];
  branchIds: string[];
}

export class SchedulingProjectFilterCascadeService {

  private tagsCascadeRequestId = 0;
  private branchesCascadeRequestId = 0;

  constructor(private groupsHttpService: GroupsHttpService, private $groups, private $log) {
  }

  invalidateTagsCascade(): void {
    this.tagsCascadeRequestId++;
  }

  applyTagsFilter(tags: string[]): Promise<SchedulingProjectTagsFilterCascadeResult> {
    const requestId = ++this.tagsCascadeRequestId;

    if (!tags?.length) {
      return Promise.resolve({
        filteredGroupIds: undefined,
        groupIds: [],
        filteredBranchIds: undefined,
        branchIds: []
      });
    }

    return firstValueFrom(this.groupsHttpService.getGroups({tag: tags, limit: 1000}))
      .then((response) => {
        if (requestId !== this.tagsCascadeRequestId) {
          return Promise.reject({stale: true});
        }
        const groupIds = _.map(response.list || [], 'id');
        return this._resolveBranchesFromGroups(groupIds, requestId)
          .then((branchState) => {
            return {
              filteredGroupIds: groupIds,
              groupIds: groupIds,
              filteredBranchIds: branchState.filteredBranchIds,
              branchIds: branchState.branchIds
            };
          });
      })
      .catch((error) => {
        if (error?.stale) {
          return Promise.reject(error);
        }
        this.$log.error('Failed to load groups by tag', error);
        return Promise.reject(error);
      });
  }

  applyBranchesFromGroups(groupIds: string[]): Promise<SchedulingProjectGroupsFilterCascadeResult> {
    this.invalidateTagsCascade();
    return this._resolveBranchesFromGroups(groupIds);
  }

  private _resolveBranchesFromGroups(groupIds: string[], tagsCascadeRequestId?: number): Promise<SchedulingProjectGroupsFilterCascadeResult> {
    const branchesRequestId = ++this.branchesCascadeRequestId;

    if (!groupIds?.length) {
      if (tagsCascadeRequestId !== undefined && tagsCascadeRequestId !== this.tagsCascadeRequestId) {
        return Promise.reject({stale: true});
      }
      return Promise.resolve({
        filteredBranchIds: undefined,
        branchIds: []
      });
    }

    return this.$groups().$asyncValues(groupIds)
      .then((groups) => {
        if (branchesRequestId !== this.branchesCascadeRequestId) {
          return Promise.reject({stale: true});
        }
        if (tagsCascadeRequestId !== undefined && tagsCascadeRequestId !== this.tagsCascadeRequestId) {
          return Promise.reject({stale: true});
        }
        let groupsBranchIds = [];
        groups.forEach((group) => {
          if (group.branchIds?.length) {
            groupsBranchIds = groupsBranchIds.concat(group.branchIds);
          }
        });
        return {
          filteredBranchIds: _.uniq(groupsBranchIds),
          branchIds: _.uniq(groupsBranchIds)
        };
      })
      .catch((error) => {
        if (error?.stale) {
          return Promise.reject(error);
        }
        this.$log.error('Failed to load groups', error);
        return Promise.reject(error);
      });
  }

}

angular
  .module('bringoz.admin.scheduling-project')
  .service('schedulingProjectFilterCascadeService', SchedulingProjectFilterCascadeService);
